import { getStandardRetryDelay, getStandardRetryLogic } from "#lib/errors"
import { homeFinancialSDK } from "#sdk/sdk"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

const queryKeys = {
  all: ["account-members"] as const,
  lists: () => [...queryKeys.all, "list"] as const,
  list: (accountId: number) => [...queryKeys.lists(), accountId] as const,
}

/**
 * Hook to fetch all members of an account
 */
export function useAccountMembers(accountId: number | null) {
  return useQuery({
    queryKey: queryKeys.list(accountId!),
    queryFn: async () => {
      if (!accountId) {
        throw new Error("Account ID is required")
      }
      return await homeFinancialSDK.accounts.getMembers(accountId)
    },
    enabled: !!accountId,
  })
}

/**
 * Hook to remove a member from an account
 */
export function useRemoveMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ accountId, userId }: { accountId: number; userId: number }) => {
      return await homeFinancialSDK.accounts.removeMember(accountId, userId)
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific account's members list
      queryClient.invalidateQueries({ queryKey: queryKeys.list(variables.accountId) })
      // Also invalidate accounts list
      queryClient.invalidateQueries({ queryKey: ["accounts"] })
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

/**
 * Hook to update a member's role
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      accountId,
      userId,
      role,
    }: {
      accountId: number
      userId: number
      role: string
    }) => {
      return await homeFinancialSDK.accounts.updateMemberRole(accountId, userId, role)
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific account's members list
      queryClient.invalidateQueries({ queryKey: queryKeys.list(variables.accountId) })
      // Also invalidate accounts list
      queryClient.invalidateQueries({ queryKey: ["accounts"] })
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}
